import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiEdit2,
  FiRefreshCw,
  FiExternalLink,
  FiMail,
  FiPhone,
  FiMapPin,
  FiClock,
  FiUsers,
  FiCheckCircle,
  FiAlertTriangle,
  FiKey,
  FiActivity,
} from "react-icons/fi";
import { IoLockClosedOutline, IoLockOpenOutline } from "react-icons/io5";
import { GoTrash } from "react-icons/go";
import { FaRegBuilding } from "react-icons/fa";
import { toast } from "react-toastify";
import { apiCall } from "../../../api/fetchClient";
import {
  GET_COMPANY_DETAILS_URL,
  SUSPEND_COMPANY_URL,
  DELETE_COMPANY_URL,
} from "../../../api/url";
import { ROUTES } from "../../../routes/routes";
import { strings } from "../../../strings/strings";
import { formatDate, formatDateTime } from "./companyUtils";
import Button from "../../../components/ui/Button";
import CompanyModal from "./CompanyModal";
import FreezeModal from "./FreezeModal";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";

const CompanyDetailPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Modals state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [freezeModal, setFreezeModal] = useState({ isOpen: false, company: null });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, company: null });

  // Fetch Company Details
  const fetchCompanyDetails = useCallback(
    async (showRefreshingSpinner = false) => {
      if (!companyId) return;

      if (showRefreshingSpinner) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await apiCall(
          `${GET_COMPANY_DETAILS_URL}${companyId}`,
          "GET"
        );

        if (response?.success && response?.results?.company) {
          setCompany(response.results.company);
        } else if (response?.company) {
          setCompany(response.company);
        } else {
          setCompany(null);
        }
      } catch {
        setCompany(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [companyId]
  );

  useEffect(() => {
    fetchCompanyDetails();
  }, [fetchCompanyDetails]);

  // Get Initials for Avatar Fallback
  const getInitials = (name) => {
    if (!name) return "CO";
    const parts = name.trim().split(/\s+/);
    if (parts.length > 1) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  };

  // Freeze / Unfreeze Handler
  const handleFreeze = async (reason) => {
    if (!company) return;
    try {
      const isCurrentlyActive = company.isActive && !company.isSuspended;
      const requestBody = isCurrentlyActive
        ? { isSuspended: true, reason }
        : { isSuspended: false };

      const response = await apiCall(
        `${SUSPEND_COMPANY_URL}${company._id}`,
        "PUT",
        requestBody
      );

      if (response?.success) {
        toast.success(
          isCurrentlyActive
            ? "Company frozen successfully!"
            : "Company unfreezed successfully!"
        );
        fetchCompanyDetails(true);
      }
    } catch {
      // Handled by apiCall toast
    } finally {
      setFreezeModal({ isOpen: false, company: null });
    }
  };

  // Delete Handler
  const handleDelete = async () => {
    if (!company) return;
    try {
      const response = await apiCall(
        `${DELETE_COMPANY_URL}${company._id}`,
        "DELETE"
      );

      if (response?.success) {
        toast.success("Company deleted successfully!");
        setConfirmModal({ isOpen: false, company: null });
        navigate(ROUTES.ADMIN_COMPANY);
      }
    } catch {
      setConfirmModal({ isOpen: false, company: null });
    }
  };

  // Loading State
  if (loading) {
    return (
      <LoadingSpinner
        message="Loading company details..."
        minHeight="min-h-[70vh]"
      />
    );
  }

  // Not Found State
  if (!company) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center text-center p-6">
        <div className="w-12 h-12 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center text-red-600 dark:text-red-400 mb-3">
          <FiAlertTriangle className="w-6 h-6" />
        </div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1.5">
          Company Not Found
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-5">
          The requested company could not be located or may have been deleted.
        </p>
        <Link to={ROUTES.ADMIN_COMPANY}>
          <Button variant="primary" className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm">
            <FiArrowLeft className="w-4 h-4" />
            Back to Companies
          </Button>
        </Link>
      </div>
    );
  }

  const isSuspended = !company.isActive || company.isSuspended;

  // Safe employee stats
  const employeeStats = company.employeeStats || {
    totalEmployees: 0,
    activeEmployees: 0,
    deactiveEmployees: 0,
  };
  const totalEmployees = Number(employeeStats.totalEmployees ?? 0);
  const activeEmployees = Number(employeeStats.activeEmployees ?? 0);
  const deactiveEmployees = Number(employeeStats.deactiveEmployees ?? 0);

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-12 animate-fadeIn">
      {/* 1. Header & Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to={ROUTES.ADMIN_COMPANY}
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:text-primary hover:border-primary/40 transition-colors shadow-2xs"
            title="Back to Company Management"
          >
            <FiArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <Link to={ROUTES.ADMIN_COMPANY} className="hover:text-primary transition-colors">
                {strings.COMPANEE_MANAGEMENT}
              </Link>
              <span>/</span>
              <span className="text-gray-900 dark:text-white font-medium truncate max-w-xs">
                {company.companyName}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
              Company Overview
            </h1>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2 flex-wrap">
         

          <button
            type="button"
            onClick={() => setIsEditModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/20 text-primary-dark dark:text-primary transition-colors"
          >
            <FiEdit2 className="w-3.5 h-3.5" />
            <span>{strings.EDIT}</span>
          </button>

          <button
            type="button"
            onClick={() => setFreezeModal({ isOpen: true, company })}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border transition-colors ${
              isSuspended
                ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100"
                : "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 hover:bg-amber-100"
            }`}
          >
            {isSuspended ? (
              <>
                <IoLockOpenOutline className="w-3.5 h-3.5" />
                <span>{strings.UNFREEZE}</span>
              </>
            ) : (
              <>
                <IoLockClosedOutline className="w-3.5 h-3.5" />
                <span>{strings.FREEZE}</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => setConfirmModal({ isOpen: true, company })}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/20 text-rose-700 dark:text-rose-400 hover:bg-rose-100 transition-colors"
          >
            <GoTrash className="w-3.5 h-3.5" />
            <span>{strings.DELETE}</span>
          </button>
        </div>
      </div>

      {/* 2. Simple & Clean Profile Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 sm:p-6 shadow-2xs">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Logo / Monogram */}
          <div className="flex-shrink-0">
            {company.companyLogo ? (
              <div className="w-16 h-16 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 shadow-2xs">
                <img
                  src={company.companyLogo}
                  alt={company.companyName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "";
                  }}
                />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-xl bg-primary/10 text-primary-dark dark:text-primary font-bold text-2xl flex items-center justify-center border border-primary/20">
                {getInitials(company.companyName)}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="min-w-0 space-y-1.5">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {company.companyName}
              </h2>

              {/* Status Badge */}
              <span
                className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                  !isSuspended
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                    !isSuspended ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                {!isSuspended ? strings.ACTIVE : strings.SUSPENDED}
              </span>

             
            </div>

            {/* Sub-info items */}
            <div className="flex items-center gap-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400 flex-wrap">
              {company.accessCompanyCode && (
                <span className="flex items-center gap-1.5 font-mono">
                  <FiKey className="w-3.5 h-3.5 text-gray-400" />
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {company.accessCompanyCode}
                  </span>
                </span>
              )}

              {company.companyEmail && (
                <span className="flex items-center gap-1.5 truncate">
                  <FiMail className="w-3.5 h-3.5 text-gray-400" />
                  <span className="truncate">{company.companyEmail}</span>
                </span>
              )}

              {company.mobileNumber && (
                <span className="flex items-center gap-1.5">
                  <FiPhone className="w-3.5 h-3.5 text-gray-400" />
                  <span>
                    {company.mobileCode || "+91"} {company.mobileNumber}
                  </span>
                </span>
              )}

              {company.companyAddress?.city && (
                <span className="flex items-center gap-1.5">
                  <FiMapPin className="w-3.5 h-3.5 text-gray-400" />
                  <span>
                    {company.companyAddress.city}
                    {company.companyAddress.country ? `, ${company.companyAddress.country}` : ""}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 3. Suspension Warning Notice (Only when suspended) */}
      {isSuspended && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <FiAlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              This company account is currently suspended. Portal access for admins and employees is frozen.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setFreezeModal({ isOpen: true, company })}
            className="self-start sm:self-center px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-amber-600 hover:bg-amber-700 text-white transition-colors shrink-0"
          >
            Unfreeze Account
          </button>
        </div>
      )}

      {/* 4. Three Key Summary Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Metric 1: Total Workforce */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-2xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Total Workforce
            </span>
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
              <FiUsers className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {totalEmployees}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Employees
            </span>
          </div>
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 text-xs">
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">
              {activeEmployees} Active
            </span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-500 dark:text-gray-400">
              {deactiveEmployees} Inactive
            </span>
          </div>
        </div>

        {/* Metric 2: Company Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-2xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Company Recent Activity
            </span>
            <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
              <FiActivity className="w-4 h-4" />
            </div>
          </div>
          <p className="text-base font-bold text-gray-900 dark:text-white truncate">
            {company.companyRecentActivity
              ? formatDateTime(company.companyRecentActivity)
              : "No activity recorded"}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 truncate">
            Admin & Management Action
          </p>
        </div>

        {/* Metric 3: Employee Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-2xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Employee Recent Activity
            </span>
            <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
              <FiClock className="w-4 h-4" />
            </div>
          </div>
          <p className="text-base font-bold text-gray-900 dark:text-white truncate">
            {company.employeeRecentActivity
              ? formatDateTime(company.employeeRecentActivity)
              : "No activity recorded"}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 truncate">
            Workforce Punch & Check-in
          </p>
        </div>
      </div>

      {/* 5. Detailed Information (Clean 2-Column Layout) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Company Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 sm:p-6 shadow-2xs space-y-4">
          <div className="flex items-center gap-2.5 pb-3 border-b border-gray-100 dark:border-gray-700">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
              <FaRegBuilding className="w-4 h-4" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Company Information
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400 block text-xs mb-0.5">
                Legal Company Name
              </span>
              <p className="font-semibold text-gray-900 dark:text-white">
                {company.companyName || "—"}
              </p>
            </div>

            <div>
              <span className="text-gray-500 dark:text-gray-400 block text-xs mb-0.5">
                Access Code
              </span>
              <p className="font-mono font-semibold text-gray-900 dark:text-white">
                {company.accessCompanyCode || "—"}
              </p>
            </div>

            <div>
              <span className="text-gray-500 dark:text-gray-400 block text-xs mb-0.5">
                Industry
              </span>
              <p className="font-semibold text-gray-900 dark:text-white">
                {company.industry || "Not Specified"}
              </p>
            </div>

            <div>
              <span className="text-gray-500 dark:text-gray-400 block text-xs mb-0.5">
                Company Size Bracket
              </span>
              <p className="font-semibold text-gray-900 dark:text-white">
                {company.companySize ? `${company.companySize} employees` : "1-10"}
              </p>
            </div>

            <div>
              <span className="text-gray-500 dark:text-gray-400 block text-xs mb-0.5">
                GST / Tax Registration ID
              </span>
              <p className="font-mono font-semibold text-gray-900 dark:text-white">
                {company.gstNo || "Not Registered"}
              </p>
            </div>

            <div>
              <span className="text-gray-500 dark:text-gray-400 block text-xs mb-0.5">
                Registration Date
              </span>
              <p className="font-semibold text-gray-900 dark:text-white">
                {formatDate(company.createdAt)}
              </p>
            </div>
          </div>
        </div>

        {/* Right Column: Contact & Address */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 sm:p-6 shadow-2xs space-y-4">
          <div className="flex items-center gap-2.5 pb-3 border-b border-gray-100 dark:border-gray-700">
            <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
              <FiPhone className="w-4 h-4" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Contact & Address
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400 block text-xs mb-0.5">
                Corporate Email
              </span>
              {company.companyEmail ? (
                <a
                  href={`mailto:${company.companyEmail}`}
                  className="font-semibold text-gray-900 dark:text-white hover:text-primary transition-colors truncate block"
                >
                  {company.companyEmail}
                </a>
              ) : (
                <p className="font-semibold text-gray-400">—</p>
              )}
            </div>

            <div>
              <span className="text-gray-500 dark:text-gray-400 block text-xs mb-0.5">
                Primary Telephone
              </span>
              {company.mobileNumber ? (
                <a
                  href={`tel:${company.mobileCode || ""}${company.mobileNumber}`}
                  className="font-semibold text-gray-900 dark:text-white hover:text-primary transition-colors font-mono block"
                >
                  {company.mobileCode || "+91"} {company.mobileNumber}
                </a>
              ) : (
                <p className="font-semibold text-gray-400">—</p>
              )}
            </div>

            <div>
              <span className="text-gray-500 dark:text-gray-400 block text-xs mb-0.5">
                Official Website
              </span>
              {company.companyWebsite ? (
                <a
                  href={
                    company.companyWebsite.startsWith("http")
                      ? company.companyWebsite
                      : `https://${company.companyWebsite}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary hover:underline flex items-center gap-1"
                >
                  <span>{company.companyWebsite}</span>
                  <FiExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <p className="font-semibold text-gray-400">Not Provided</p>
              )}
            </div>


            <div className="sm:col-span-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              <span className="text-gray-500 dark:text-gray-400 block text-xs mb-0.5">
                Office Address
              </span>
              <p className="font-semibold text-gray-900 dark:text-white leading-relaxed">
                {[
                  company.companyAddress?.addressLine1,
                  company.companyAddress?.addressLine2,
                  company.companyAddress?.city,
                  company.companyAddress?.state,
                  company.companyAddress?.country,
                ]
                  .filter((p) => p && String(p).trim() !== "")
                  .join(", ") || "—"}
                {company.companyAddress?.zipCode ? ` - ${company.companyAddress.zipCode}` : ""}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Company Modal */}
      <CompanyModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={() => {
          fetchCompanyDetails(true);
          setIsEditModalOpen(false);
        }}
        company={company}
      />

      {/* Freeze / Unfreeze Modal */}
      <FreezeModal
        isOpen={freezeModal.isOpen}
        onClose={() => setFreezeModal({ isOpen: false, company: null })}
        onConfirm={handleFreeze}
        company={freezeModal.company}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, company: null })}
        onConfirm={handleDelete}
        title={strings.DELETE_TITLE}
        message={strings.DELETE_MESSAGE(company?.companyName)}
        confirmText={strings.DELETE}
        variant="danger"
        icon={GoTrash}
      />
    </div>
  );
};

export default CompanyDetailPage;
